import { Link } from 'react-router';

import LazyVideo from '@cartwhl/ui/atoms/LazyVideo';
import Text from '@cartwhl/ui/base/TextNew';

import { type ArticleHero, type ArticleMeta, CATEGORY_LABELS } from '../types';
import { BLOG_ARTICLE_CDN_BASE } from './cdn';

function formatDate(dateString: string): string {
  const date = new Date(`${dateString}T00:00:00`);

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getCategoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

function getMediaSource(
  hero: Exclude<ArticleHero, { type: 'youtube' }>,
): string {
  if ('src' in hero && hero.src) {
    return hero.src;
  }

  return `${BLOG_ARTICLE_CDN_BASE}${hero.cdnKey}`;
}

interface ArticleMediaProps {
  article: ArticleMeta;
}

function ArticleMedia({ article }: ArticleMediaProps) {
  const mediaClassName =
    'h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.015]';

  if (!article.hero) {
    return (
      <div className="bg-green-10 flex h-full w-full items-end p-6 lg:p-8">
        <Text token="display/20/bold" className="text-green-50">
          {getCategoryLabel(article.category)}
        </Text>
      </div>
    );
  }

  if (article.hero.type === 'youtube') {
    return (
      <img
        src={`https://img.youtube.com/vi/${article.hero.videoId}/maxresdefault.jpg`}
        alt=""
        loading="lazy"
        className={mediaClassName}
      />
    );
  }

  const mediaSource = getMediaSource(article.hero);

  if (article.hero.type === 'image') {
    return (
      <img src={mediaSource} alt="" loading="lazy" className={mediaClassName} />
    );
  }

  return (
    <LazyVideo src={mediaSource} mode="autoplay" className={mediaClassName} />
  );
}

interface ArticleDetailsProps {
  article: ArticleMeta;
}

function ArticleDetails({ article }: ArticleDetailsProps) {
  return (
    <div className="flex flex-1 flex-col">
      <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1">
        <Text token="14/medium" className="text-green-50">
          {getCategoryLabel(article.category)}
        </Text>
        <Text
          as="time"
          dateTime={article.date}
          token="14"
          className="text-tertiary"
        >
          {formatDate(article.date)}
        </Text>
      </div>

      <Text as="h2" token="24/medium" className="text-pretty">
        {article.title}
        {article.subtitle ? ` — ${article.subtitle}` : ''}
      </Text>

      {article.summary && (
        <Text as="p" token="16" className="text-secondary mt-4 text-pretty">
          {article.summary}
        </Text>
      )}

      {article.author && article.author !== 'Cartwheel' && (
        <Text as="p" token="14/medium" className="text-secondary mt-5">
          By {article.author}
        </Text>
      )}
    </div>
  );
}

interface ArticleCardProps {
  article: ArticleMeta;
}

function ArticleCard({ article }: ArticleCardProps) {
  return (
    <li className="col-span-12 md:col-span-6 lg:col-span-4">
      <Link
        to={`/blog/${article.slug}`}
        className="focus-visible group flex h-full flex-col rounded-2xl"
      >
        <div className="bg-secondary group-hover:shadow-menu mb-6 aspect-video overflow-hidden rounded-2xl transition-shadow">
          <ArticleMedia article={article} />
        </div>
        <ArticleDetails article={article} />
      </Link>
    </li>
  );
}

interface BlogLandingPageProps {
  articles: ArticleMeta[];
}

export default function BlogLandingPage({ articles }: BlogLandingPageProps) {
  const sortedArticles = [...articles].sort(
    (articleA, articleB) =>
      articleB.date.localeCompare(articleA.date) ||
      articleA.title.localeCompare(articleB.title),
  );

  return (
    <div className="container pb-24 pt-10 lg:pb-36 lg:pt-20">
      <header className="flex flex-col gap-7 pb-14 lg:gap-20 lg:pb-20">
        <Text
          as="h1"
          token={{ base: 'display/36/bold', lg: 'display/64/bold' }}
          className="text-pretty"
        >
          Blog
        </Text>
        <Text
          as="p"
          token={{ base: '20/medium', lg: '36/medium' }}
          className="text-pretty"
        >
          Notes from the team building Cartwheel
        </Text>
      </header>

      <section>
        <ul className="grid grid-cols-12 gap-x-6 gap-y-14 lg:gap-x-8 lg:gap-y-20">
          {sortedArticles.map((article) => (
            <ArticleCard key={article.slug} article={article} />
          ))}
        </ul>
      </section>
    </div>
  );
}
